import { type FC, type KeyboardEvent, type FocusEvent, useCallback, type ChangeEvent, useMemo } from 'react';

import { InputAdornmentComponent } from '@vakantiesnl/components/src/atoms/InputAdornment';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { TextFieldComponent } from '@vakantiesnl/components/src/molecules/TextField';
import type { MicroCopy } from '@vakantiesnl/types';

import { useStyles } from './PriceRangeFilter.style';

interface Props {
	microCopies: MicroCopy;
	priceFilterDefaultMinimum: number;
	minimumPrice: number | undefined;
	maximumPrice: number | undefined;
	handleMinPriceChange: (_id: string, value: string) => void;
	handleMaxPriceChange: (_id: string, value: string) => void;
	priceFilterError: string | undefined;
	handleMinPriceKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
	handleMaxPriceKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
	handlePriceContainerBlur: (event: FocusEvent<HTMLDivElement>) => void;
	errorTextClassName?: string;
	disabled?: boolean;
}

export const PriceRangeFilter: FC<Props> = ({
	microCopies,
	priceFilterDefaultMinimum,
	priceFilterError,
	minimumPrice,
	maximumPrice,
	handleMinPriceChange,
	handleMaxPriceChange,
	handleMinPriceKeyDown,
	handleMaxPriceKeyDown,
	handlePriceContainerBlur,
	errorTextClassName,
	disabled,
}) => {
	const { classes, cx } = useStyles();

	const onMinPriceChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			handleMinPriceChange('min-price', e.currentTarget.value);
		},
		[handleMinPriceChange],
	);

	const onMaxPriceChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			handleMaxPriceChange('max-price', e.currentTarget.value);
		},
		[handleMaxPriceChange],
	);

	const textFieldInputProps = useMemo(() => {
		return {
			startAdornment: <InputAdornmentComponent position="start">€</InputAdornmentComponent>,
		};
	}, []);

	return (
		<div>
			<div className={classes.priceRangeContainer}>
				<div className={classes.priceRangeBlurContainer} onBlur={handlePriceContainerBlur}>
					<TextFieldComponent
						type="text"
						id="min-price"
						value={minimumPrice?.toString() ?? ''}
						placeholder={priceFilterDefaultMinimum.toString()}
						className={classes.priceInput}
						onChange={onMinPriceChange}
						onKeyDown={handleMinPriceKeyDown}
						error={Boolean(priceFilterError)}
						disabled={disabled}
						InputProps={textFieldInputProps}
					/>
					<Typography as="span" variant="bodyMd">
						{microCopies['filters.priceRange.to']}
					</Typography>
					<TextFieldComponent
						type="text"
						id="max-price"
						value={maximumPrice?.toString() ?? ''}
						placeholder="1000+"
						className={classes.priceInput}
						onChange={onMaxPriceChange}
						onKeyDown={handleMaxPriceKeyDown}
						error={Boolean(priceFilterError)}
						disabled={disabled}
						InputProps={textFieldInputProps}
					/>
				</div>
			</div>
			<div className={cx(classes.priceRangeValidationErrorText, errorTextClassName)}>
				{priceFilterError ? (
					<Typography as="span" variant="bodyMd">
						{microCopies['filters.priceRange.validationError']}
					</Typography>
				) : null}
			</div>
		</div>
	);
};
